/**
 * Learnings Service - API functions for classification rules and counterparty mappings
 */

import { apiClient } from '@/lib/api'

export interface ClassificationRule {
  id: string
  client_id: string
  client_name: string | null
  counterparty_vat: string | null
  counterparty_name_normalized: string | null
  product_pattern: string | null
  rule_text: string
  erp_data: Record<string, any>
  is_active: boolean
  times_applied: number
  times_confirmed: number
  times_overridden: number
  created_at: string
  updated_at: string
}

export interface ClassificationRuleVersion {
  id: string
  rule_id: string
  version: number
  change_type: string
  rule_text: string
  erp_data: Record<string, any>
  reason: string | null
  source_invoice_id: string | null
  created_at: string
}

export interface CounterpartyMapping {
  id: string
  client_id: string
  client_name: string | null
  counterparty_vat: string
  counterparty_name_normalized: string
  mirage_counterparty_id: number
  created_at: string
  updated_at: string
}

export async function getClassificationRules(params: {
  client_id?: string
  counterparty_vat?: string
  search?: string
  is_active?: boolean
  page?: number
  page_size?: number
}): Promise<{ rules: ClassificationRule[]; total: number }> {
  const searchParams = new URLSearchParams()
  if (params.client_id) searchParams.set('client_id', params.client_id)
  if (params.counterparty_vat) searchParams.set('counterparty_vat', params.counterparty_vat)
  if (params.search) searchParams.set('search', params.search)
  if (params.is_active !== undefined) searchParams.set('is_active', String(params.is_active))
  if (params.page) searchParams.set('page', String(params.page))
  if (params.page_size) searchParams.set('page_size', String(params.page_size))
  return apiClient.get(`/classification-rules/?${searchParams.toString()}`)
}

export async function getRuleVersions(ruleId: string): Promise<ClassificationRuleVersion[]> {
  return apiClient.get(`/classification-rules/${ruleId}/versions`)
}

export async function updateClassificationRule(
  ruleId: string,
  data: { rule_text?: string; erp_data?: Record<string, any>; is_active?: boolean }
): Promise<ClassificationRule> {
  return apiClient.patch(`/classification-rules/${ruleId}`, data)
}

export async function deleteClassificationRule(ruleId: string): Promise<void> {
  return apiClient.delete(`/classification-rules/${ruleId}`)
}

export async function getCounterpartyMappings(params: {
  client_id?: string
  search?: string
  page?: number
  page_size?: number
}): Promise<{ mappings: CounterpartyMapping[]; total: number }> {
  const searchParams = new URLSearchParams()
  if (params.client_id) searchParams.set('client_id', params.client_id)
  if (params.search) searchParams.set('search', params.search)
  if (params.page) searchParams.set('page', String(params.page))
  if (params.page_size) searchParams.set('page_size', String(params.page_size))
  return apiClient.get(`/counterparty-mappings/?${searchParams.toString()}`)
}

export async function deleteCounterpartyMapping(mappingId: string): Promise<void> {
  return apiClient.delete(`/counterparty-mappings/${mappingId}`)
}

// ─── Client Identifications ──────────────────────────────────────────────────

export interface ClientIdentificationItem {
  id: string
  client_id: string
  client_name: string | null
  identifier_type: string
  identifier_value: string
  created_at: string
}

export async function getClientIdentifications(params: {
  client_id?: string
  search?: string
  page?: number
  page_size?: number
}): Promise<{ identifications: ClientIdentificationItem[]; total: number }> {
  const searchParams = new URLSearchParams()
  if (params.client_id) searchParams.set('client_id', params.client_id)
  if (params.search) searchParams.set('search', params.search)
  if (params.page) searchParams.set('page', String(params.page))
  if (params.page_size) searchParams.set('page_size', String(params.page_size))
  return apiClient.get(`/client-identifications/?${searchParams.toString()}`)
}

export async function deleteClientIdentification(id: string): Promise<void> {
  return apiClient.delete(`/client-identifications/${id}`)
}

